import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { themes, type ThemeMode } from './theme';

interface ThemeModeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

const STORAGE_KEY = 'auto-threds-theme-mode';

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [storedMode, setMode] = useLocalStorage<ThemeMode>(STORAGE_KEY, 'light');
  const mode: ThemeMode =
    storedMode === 'light' || storedMode === 'dark' ? storedMode : 'light';

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, [setMode]);

  const value = useMemo(
    () => ({
      mode,
      setMode: (next: ThemeMode) => setMode(next),
      toggleMode,
    }),
    [mode, setMode, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={themes[mode]}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useAppTheme() {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return ctx;
}
